import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { prop } from 'ramda';
import moment from 'moment';

import { ConfirmModal, FormsCollection } from '@/ui-kit';
import SprintForm from '../sprintForm';

const CompleteSprintModal = ({ isOpen, isPending, onClose, sprint, onComplete }) => {
  const [action, setAction] = useState(null);

  const [formValues, setFormValues] = useState({
    title: '',
    description: '',
    startDate: null,
    startTime: { hours: 0, minutes: 0 },
    finishDate: null,
    finishTime: { hours: 0, minutes: 0 },
  });
  const [formErrors, setFormErrors] = useState({});

  const isActionDisabled = action === null || (action === 'new_sprint' && !formValues.title.trim());
  const isNewSprintSelected = action === 'new_sprint';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'title' && !value.trim()) {
      setFormErrors((prev) => ({
        ...prev,
        title: { message: 'Title is required' },
      }));
    }
  };

  const onChangeStartDate = (e) => {
    const value = e.target.value;
    setFormValues((prev) => ({
      ...prev,
      startDate: value,
      finishDate: value ? moment(value).add(14, 'day').toDate() : '',
    }));
  };

  const getValidFinishDate = () => {
    const { finishDate, startDate } = formValues;
    if (!finishDate) return '';
    if (finishDate < startDate) {
      return moment(startDate).add(1, 'day').toDate();
    }
    return finishDate;
  };

  const handleCompleteAction = () => {
    if (action === 'new_sprint') {
      const errors = {};
      if (!formValues.title.trim()) {
        errors.title = { message: 'Title is required' };
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      onComplete({
        newSprintData: formValues,
        action
      });
      onClose();
    } else {
      onComplete({
        action
      });
      onClose();
    }
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      isPending={isPending}
      actionTitle="Complete"
      onAction={handleCompleteAction}
      onClose={onClose}
      title="Complete Sprint"
      disabledAction={isActionDisabled}
    >
      <div className="complete-sprint__text">
        <span>
          You are about to complete <b>{prop('title', sprint)}</b> sprint?
        </span>
        <span>Choose what to do with the unfinished tasks:</span>
      </div>
      <div className="complete-sprint__radio">
        <FormsCollection.Radio
          label="Create new sprint and move them there"
          value="new_sprint"
          checked={action === 'new_sprint'}
          onChange={() => setAction('new_sprint')}
          id="create-channel-modal__privacy--public"
        />
        <FormsCollection.Radio
          label="Move them to backlog"
          value="backlog"
          checked={action === 'backlog'}
          onChange={() => setAction('backlog')}
          id="create-channel-modal__privacy--private"
        />
        <FormsCollection.Radio
          label="Do nothing"
          value="do_nothing"
          checked={action === 'do_nothing'}
          onChange={() => setAction('do_nothing')}
          id="create-channel-modal__privacy--do_nothing"
        />
      </div>

      {isNewSprintSelected && (
        <div className="complete-sprint__new-sprint-form">
          <h4>Create new sprint</h4>
          <SprintForm
            handleChange={handleChange}
            handleBlur={handleBlur}
            values={formValues}
            errors={formErrors}
            getValidFinishDate={getValidFinishDate}
            onChangeStartDate={onChangeStartDate}
            startDateId="new-sprint__start-date"
            finishDateId="new-sprint__finish-date"
          />
        </div>
      )}
    </ConfirmModal>
  );
};

CompleteSprintModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isPending: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sprint: PropTypes.instanceOf(Object).isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default CompleteSprintModal;
